import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { Functie } from "../../../model";
import { FavorietenChangeRequest } from "../../../model/favoriet/favoriet";
import { ContentService } from "../../../services/content/content.service";
import { ProfileStorageService } from "../../../services/profile/profile-storage.service";
import { FavorietenResource } from "../../../services/resources/favorieten.resource";
import { StorageService } from "../../../services/storage/storage.service";
import { UtilService } from "../../../services/util/util.service";
import { FunctieCardEnum } from "../card/functie-card.enum";

const content = require("./functie-rooster.content.json");

@Component({
  selector: "app-functie-rooster",
  templateUrl: "./functie-rooster.component.html",
  styleUrls: ["./functie-rooster.component.scss"],
})
export class FunctieRoosterComponent implements OnInit, OnDestroy {
  @Input()
  public mode: string = FunctieCardEnum.DEFAULT;
  @Input()
  public functies: Functie[] = [];
  @Input()
  public loading: boolean = true;
  @Input()
  public showMatchPercentage: boolean = false;

  @Output()
  public getMore: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  public sort: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  public select: EventEmitter<Functie> = new EventEmitter();
  @Output()
  public changeFunction: EventEmitter<Functie> = new EventEmitter();
  @Output()
  public preview: EventEmitter<Functie> = new EventEmitter();

  public FunctieCardEnum = FunctieCardEnum;
  public subscriptions: Subscription[] = [];
  public isMobile: boolean = false;
  public isLoggedIn: boolean = false;
  private getMoreThreshold: number = 400;

  public constructor(
    private profileStorageService: ProfileStorageService,
    private favorietenResource: FavorietenResource,
    private matDialog: MatDialog,
    private utilService: UtilService,
    private contentService: ContentService,
    private storageService: StorageService
  ) {
    contentService.loadContent(content);
  }

  public ngOnInit() {
    this.subscriptions.push(
      this.profileStorageService.loggedInSubject.subscribe(
        (isLoggedIn: boolean) => {
          if (this.isLoggedIn !== isLoggedIn) {
            this.isLoggedIn = isLoggedIn;
          }
        }
      )
    );

    this.subscriptions.push(
      this.utilService.getScreenMode$().subscribe((screenMode) => {
        this.isMobile = screenMode.includes("MOBILE");
      })
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }

  @HostListener("window:scroll", ["$event"])
  public onWindowScroll() {
    const pos =
      (document.documentElement.scrollTop || document.body.scrollTop) +
      document.documentElement.offsetHeight;
    const max = document.documentElement.scrollHeight;
    if (pos >= max - this.getMoreThreshold) {
      this.getMore.next();
    }
  }

  public sortBy(event): void {
    this.sort.next(event.value);
  }

  public handleSelect($event): void {
    this.select.emit($event);
  }

  public handleChangeFunction(): void {
    this.changeFunction.emit();
  }

  public handlePreviewClick(functie: Functie): void {
    this.preview.emit(functie);
  }

  public handleFavoriteClick(functie: Functie): void {
    if (this.isLoggedIn) {
      this.processFunctieFavorite(functie);
    } else {
      this.storageService.set(
        "lbp-pending-favorite",
        {
          isFunctie: true,
          id: functie.id,
        },
        true
      );
      this.openLoginModal();
    }
  }

  private processFunctieFavorite(functie: Functie): void {
    if (functie.favoriet) {
      this.favorietenResource
        .deleteFavorietenChange({
          functies: [functie.id + ""],
        } as FavorietenChangeRequest)
        .subscribe(() => {
          functie.favoriet = false;
        });
    } else {
      this.favorietenResource
        .postFavorietenChange({
          functies: [functie.id + ""],
        } as FavorietenChangeRequest)
        .subscribe(() => {
          functie.favoriet = true;
        });
    }
  }

  private async openLoginModal(): Promise<void> {
    const { LoginModalComponent } = await import(
      "../../account/login-modal/login-modal.component"
    );
    const config: MatDialogConfig = this.isMobile
      ? {
          height: "100%",
          width: "100%",
          maxHeight: "100%",
          maxWidth: "100%",
        }
      : {
          width: "380px",
          maxWidth: "100%",
        };

    const ref: MatDialogRef<any> = this.matDialog.open(
      LoginModalComponent,
      config
    );
    ref.componentInstance.rerouteEnabled = true;
    ref.componentInstance.introMessage = this.contentService.getContent(
      "functie-rooster.loginIntroMessage"
    );
  }
}
